import { createFormatter, type Formatter } from "@kovernow/flows/format";
import { ApiError, type RecoveryAction } from "@kovernow/flows/recovery";
import { displayValue, itemName, vaultQueryKeys, type VaultItem } from "@kovernow/flows/vault";
import { homeQueryKeys } from "@kovernow/flows/home";
import { Button } from "@kovernow/react-ui/components/button";
import { useQuery, type QueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import { Page } from "../../components/Page";
import { useServices } from "../../core/app-context";
import { useT } from "../../core/use-t";

/** `GET /items/:id` answered 404: missing, or not the caller's. */
export function isNotFound(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404;
}

/** `PUT /items` / `DELETE /items/:id` refused because the item has (had) cover. */
export function isItemHasPolicy(err: unknown): boolean {
  return err instanceof ApiError && err.status === 409 && err.problem?.errorCode === "item_has_policy";
}

/** The item's query (`["vault","item",id]`); a 404 is final, other failures retry once. */
export function useItem(id: string) {
  const { vaultApi } = useServices();
  return useQuery({
    queryKey: vaultQueryKeys.item(id),
    queryFn: () => vaultApi.get(id),
    retry: (n, err) => !isNotFound(err) && n < 1,
  });
}

/** After a server-confirmed change: the item from the response, then the Vault and Home. */
export function storeUpdatedItem(queryClient: QueryClient, item: VaultItem) {
  queryClient.setQueryData(vaultQueryKeys.item(item._id), item);
  void queryClient.invalidateQueries({ queryKey: vaultQueryKeys.items() });
  void queryClient.invalidateQueries({ queryKey: homeQueryKeys.dashboard() });
}

/** The item's display name (literal, or the translated category label). */
export function useItemName() {
  const t = useT();
  return (item: VaultItem) => {
    const n = itemName(item);
    return "text" in n ? n.text : t(n.labelKey);
  };
}

/** The formatter for an item's amounts: its own currency when it differs from the tenant's. */
export function useItemFormatter() {
  const { config, format } = useServices();
  return (item: VaultItem): Formatter => {
    const cur = item.valueCurrency;
    return !cur || cur === config.currency
      ? format
      : createFormatter({ locale: config.locale, currency: cur, timeZone: config.timeZone });
  };
}

/** `displayValue` through `format`, in the item's own currency when it differs from the tenant's. */
export function useItemValue() {
  const formatterFor = useItemFormatter();
  return (item: VaultItem): string | undefined => {
    const v = displayValue(item);
    return v == null ? undefined : formatterFor(item).currency(v);
  };
}

/** "We can't find this item", with a link back to the Vault. */
export function ItemNotFound() {
  const t = useT();
  return (
    <Page titleKey="itemDetail.notFound.title" flowKey="nav.vault">
      <div data-testid="item-not-found" className="flex flex-col gap-6">
        <p className="text-muted-foreground">{t("itemDetail.notFound.body")}</p>
        <Button asChild variant="cta" size="touch" className="self-start">
          <Link to="/vault">{t("itemDetail.notFound.back")}</Link>
        </Button>
      </div>
    </Page>
  );
}

/** `item_has_policy` as a RecoveryAction: retrying can't help, so the next step is support. */
export function itemHasPolicyAction(err: ApiError): RecoveryAction {
  return {
    errorCode: "item_has_policy",
    action: "contact_support",
    kind: "nonRecoverable",
    messageKey: "errors.item_has_policy",
    correlationId: err.correlationId,
  };
}

/** Navigation state the edit route hands back to detail. */
export interface DetailNavState {
  notice?: "item_has_policy";
}
